import React from 'react';
import './ActionHistoryTooltip.css';
import { getAvatarImage, getOpponentAvatarImage } from './avatars';
import { useTranslation } from 'react-i18next';

function ActionHistoryTooltip({ action, gameState, userId, opponentId, position }) {
  const { t } = useTranslation();
  // Support both old format (actionItem/targets) and new format (unit/killer/attacker/enhancer/drone/weapon/target/message)
  const actionItem = action.actionItem || action.unit || action.killer || action.attacker || action.enhancer || action.drone || action.weapon;
  const targets = action.targets || (action.target ? [action.target] : []);
  const { type, message } = action;
  
  // For deployment actions, extract the action text (e.g., "has been deployed in Front")
  // from messages like "Stinger has been deployed in Front"
  let deploymentActionText = null;
  const isGroupedDeployment = type === 'unit-deployed' && action.isGrouped && targets.length > 0;
  
  if ((type === 'unit-deployed' || type === 'drone-deployed') && message && actionItem && !isGroupedDeployment) {
    // Remove unit/drone name from message to get just the action text
    const itemName = actionItem.name;
    if (message.startsWith(itemName + ' ')) {
      deploymentActionText = message.substring(itemName.length + 1); // +1 for the space
    }
  }
  
  // For attack actions, check if we have a target to display
  const isAttackAction = type === 'unit-attacked' && actionItem && targets.length > 0;
  
  // For kill actions, check if we have a killer and target to display
  const isKillAction = type === 'unit-killed' && actionItem && targets.length > 0;
  
  // For enhanced actions, check if we have an enhancer and target(s) to display
  const isEnhancedAction = type === 'unit-enhanced' && actionItem && (action.target || (action.targets && action.targets.length > 0));
  
  // For drone charge actions, check if we have a drone and target(s) to display
  const isDroneChargeAction = type === 'drone-charge-used' && (action.drone || action.enhancer) && (action.target || (action.targets && action.targets.length > 0));
  
  // For weapon effect actions (weapon deploy effects)
  const weaponOrEnhancer = action.weapon || action.enhancer;
  const isWeaponEffectAction = type === 'weapon-effect' && weaponOrEnhancer && weaponOrEnhancer.id && (action.target || (action.targets && action.targets.length > 0));
  
  // For drone deployment actions
  const isDroneDeployedAction = type === 'drone-deployed' && (action.drone || actionItem);
  
  // For weapon equipping actions
  const isWeaponEquippedAction = type === 'weapon-equipped' && action.weapon && action.target;
  
  // For order provided actions
  const isOrderProvidedAction = type === 'order-provided' && action.source && action.order;
  
  // For strategem requested actions
  const isStrategemRequestedAction = type === 'strategem-requested' && action.order;
  
  // For general interaction actions
  const isGeneralInteractionAction = type === 'general-interaction' && action.source;
  
  // Helper to get general avatar image
  const getGeneralAvatarImage = (targetPlayerId) => {
    if (!gameState || !targetPlayerId) return '/images/human_1.png';
    
    // Detect autobattler/spectator mode (same logic as BattleUI.js)
    const isAutobattlerMode = gameState.isAutobattler || gameState.devAutobattlerMode;
    const isSpectatorMode = gameState.spectatorUserId && gameState.spectatorUserId === userId;
    const shouldUsePlayerAvatars = isAutobattlerMode || isSpectatorMode;
    
    // Check if it's the opponent's general
    if (targetPlayerId === opponentId) {
      let opponentAvatars = {};
      if (shouldUsePlayerAvatars) {
        // In autobattler/spectator mode, opponent avatars are in playerAvatars keyed by opponent's ID
        opponentAvatars = gameState.playerAvatars?.[targetPlayerId] || {};
      } else {
        // In normal mode, opponent avatars are keyed by userId
        opponentAvatars = gameState.opponentAvatars?.[userId] || {};
      }
      
      const opponentState = gameState.state?.[targetPlayerId];
      const opponentGeneral = opponentAvatars.general || 
                              opponentState?.selectedGeneral ||
                              gameState.opponentGeneral || '';
      return getOpponentAvatarImage(opponentGeneral);
    } else {
      // It's the player's general
      // playerAvatars is keyed by playerId
      const playerAvatars = gameState.playerAvatars?.[targetPlayerId] || {};
      const playerState = gameState.state?.[targetPlayerId];
      const playerGeneral = playerAvatars.general || 
                           playerState?.selectedGeneral || '';
      return getAvatarImage(playerGeneral);
    }
  };

  return (
    <div 
      className="action-history-tooltip"
      style={{
        position: 'fixed',
        top: position?.top || 0,
        left: position?.left || 0,
        transform: 'translateX(-50%)'
      }}
    >
      <div className="tooltip-content">
        {/* Drone charge action layout: Drone | effect text | Target(s) */}
        {isDroneChargeAction ? (
          <>
            {/* Drone image + name */}
            <div className="tooltip-action-item">
              <div className="tooltip-target-image-wrapper">
                <img
                  src={`/images/${(action.drone || action.enhancer).id}.jpg`}
                  alt={(action.drone || action.enhancer).name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="tooltip-item-name">{(action.drone || action.enhancer).name}</div>
            </div>
            
            {/* Effect text based on effect type */}
            <div className="tooltip-enhance-text" style={{
              display: 'flex',
              alignItems: 'center',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'normal',
              padding: '8px 0'
            }}>
              {action.effect?.effectType === 'ability' && t('battleUI.actionHistory.gaveAbilityTo', { ability: action.effect.ability })}
              {action.effect?.effectType === 'stat' && (
                (() => {
                  const attackText = action.effect.attackChange > 0 ? `+${action.effect.attackChange}` : `${action.effect.attackChange}`;
                  const healthText = action.effect.healthChange > 0 ? `+${action.effect.healthChange}` : `${action.effect.healthChange}`;
                  if (action.effect.attackChange === 0 && action.effect.healthChange !== 0) {
                    return t('battleUI.actionHistory.providedHealthTo', { health: healthText });
                  } else if (action.effect.healthChange === 0 && action.effect.attackChange !== 0) {
                    return t('battleUI.actionHistory.providedAttackTo', { attack: attackText });
                  } else {
                    return t('battleUI.actionHistory.providedStatsTo', { attack: attackText, health: healthText });
                  }
                })()
              )}
              {action.effect?.effectType === 'status' && (
                action.effect.statuses && action.effect.statuses.length > 1
                  ? action.effect.statuses.map(s => {
                      if (s === 'confused') return t('battleUI.actionHistory.confused');
                      if (s === 'immobilized') return t('battleUI.actionHistory.immobilized');
                      return s;
                    }).join(' and ')
                  : (() => {
                      const status = action.effect.status || action.effect.statuses?.[0];
                      if (status === 'confused') return t('battleUI.actionHistory.confused');
                      if (status === 'immobilized') return t('battleUI.actionHistory.immobilized');
                      return status || t('battleUI.actionHistory.affected');
                    })()
              )}
              {action.effect?.effectType === 'damage' && t('battleUI.actionHistory.dealtDamageTo', { amount: action.effect.amount })}
              {action.effect?.effectType === 'shield' && t('battleUI.actionHistory.gaveShieldTo')}
            </div>
            
            {/* Target(s) - show all targets for grouped effects */}
            {targets && targets.length > 1 ? (
              // Multiple targets - show all in a row
              <div className="tooltip-targets">
                {targets.map((target, idx) => (
                  <div key={idx} className="tooltip-target-item">
                    <div className="tooltip-target-image-wrapper">
                      <img
                        src={`/images/${target.id}.jpg`}
                        alt={target.name || t('battleUI.actionHistory.target')}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {/* Health display for damage effects or stat effects that reduce health */}
                      {(() => {
                        const isDamageEffect = action.effect?.effectType === 'damage';
                        const isHealthReducingStatEffect = action.effect?.effectType === 'stat' && action.effect?.healthChange < 0;
                        const hasHealthInfo = target.healthBefore !== undefined || target.damageTaken !== undefined;
                        
                        if ((isDamageEffect || isHealthReducingStatEffect) && hasHealthInfo) {
                          return (
                            <div className="action-health-display action-health-target">
                              {target.healthBefore !== undefined && (
                                <span className="health-before">{target.healthBefore}</span>
                              )}
                              {target.damageTaken !== undefined && target.damageTaken > 0 && (
                                <span className="health-damage">-{target.damageTaken}</span>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="tooltip-item-name">{target.name || t('battleUI.actionHistory.target')}</div>
                  </div>
                ))}
              </div>
            ) : targets && targets.length === 1 ? (
              // Single target - show image + name
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${targets[0].id}.jpg`}
                    alt={targets[0].name || t('battleUI.actionHistory.target')}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Health display for damage effects or stat effects that reduce health */}
                  {(() => {
                    const isDamageEffect = action.effect?.effectType === 'damage';
                    const isHealthReducingStatEffect = action.effect?.effectType === 'stat' && action.effect?.healthChange < 0;
                    const hasHealthInfo = targets[0]?.healthBefore !== undefined || targets[0]?.damageTaken !== undefined;
                    
                    if ((isDamageEffect || isHealthReducingStatEffect) && hasHealthInfo) {
                      return (
                        <div className="action-health-display action-health-target">
                          {targets[0].healthBefore !== undefined && (
                            <span className="health-before">{targets[0].healthBefore}</span>
                          )}
                          {targets[0].damageTaken !== undefined && targets[0].damageTaken > 0 && (
                            <span className="health-damage">-{targets[0].damageTaken}</span>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div className="tooltip-item-name">{targets[0].name || t('battleUI.actionHistory.target')}</div>
              </div>
            ) : action.target ? (
              // Fallback: single target from action.target
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${action.target.id}.jpg`}
                    alt={action.target.name || t('battleUI.actionHistory.target')}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Health display for damage effects or stat effects that reduce health */}
                  {(() => {
                    const isDamageEffect = action.effect?.effectType === 'damage';
                    const isHealthReducingStatEffect = action.effect?.effectType === 'stat' && action.effect?.healthChange < 0;
                    const hasHealthInfo = action.target?.healthBefore !== undefined || action.target?.damageTaken !== undefined;
                    
                    if ((isDamageEffect || isHealthReducingStatEffect) && hasHealthInfo) {
                      return (
                        <div className="action-health-display action-health-target">
                          {action.target.healthBefore !== undefined && (
                            <span className="health-before">{action.target.healthBefore}</span>
                          )}
                          {action.target.damageTaken !== undefined && action.target.damageTaken > 0 && (
                            <span className="health-damage">-{action.target.damageTaken}</span>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div className="tooltip-item-name">{action.target.name || t('battleUI.actionHistory.target')}</div>
              </div>
            ) : null}
          </>
        ) : isWeaponEffectAction ? (
          <>
            {/* Weapon image + name */}
            {weaponOrEnhancer && (
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${weaponOrEnhancer.id}.jpg`}
                    alt={weaponOrEnhancer.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="tooltip-item-name">{weaponOrEnhancer.name}</div>
              </div>
            )}
            
            {/* Effect text */}
            <div className="tooltip-enhance-text" style={{
              display: 'flex',
              alignItems: 'center',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'normal',
              padding: '8px 0'
            }}>
              {action.effect?.effectType === 'ability' && t('battleUI.actionHistory.gaveAbilityTo', { ability: action.effect.ability })}
              {action.effect?.effectType === 'stat' && (
                (() => {
                  const attackText = action.effect.attackChange > 0 ? `+${action.effect.attackChange}` : `${action.effect.attackChange}`;
                  const healthText = action.effect.healthChange > 0 ? `+${action.effect.healthChange}` : `${action.effect.healthChange}`;
                  if (action.effect.attackChange === 0 && action.effect.healthChange !== 0) {
                    return t('battleUI.actionHistory.providedHealthTo', { health: healthText });
                  } else if (action.effect.healthChange === 0 && action.effect.attackChange !== 0) {
                    return t('battleUI.actionHistory.providedAttackTo', { attack: attackText });
                  } else {
                    return t('battleUI.actionHistory.providedStatsTo', { attack: attackText, health: healthText });
                  }
                })()
              )}
              {action.effect?.effectType === 'status' && (
                action.effect.statuses && action.effect.statuses.length > 1
                  ? action.effect.statuses.map(s => {
                      if (s === 'confused') return t('battleUI.actionHistory.confused');
                      if (s === 'immobilized') return t('battleUI.actionHistory.immobilized');
                      return s;
                    }).join(' and ')
                  : (() => {
                      const status = action.effect.status || action.effect.statuses?.[0];
                      if (status === 'confused') return t('battleUI.actionHistory.confused');
                      if (status === 'immobilized') return t('battleUI.actionHistory.immobilized');
                      return status || t('battleUI.actionHistory.affected');
                    })()
              )}
              {action.effect?.effectType === 'damage' && t('battleUI.actionHistory.dealtDamageTo', { amount: action.effect.amount })}
              {action.effect?.effectType === 'shield' && t('battleUI.actionHistory.gaveShieldTo')}
            </div>
            
            {/* Target unit(s) */}
            {targets && targets.length > 0 ? (
              targets.length === 1 ? (
                <div className="tooltip-action-item">
                  <div className="tooltip-target-image-wrapper">
                    <img
                      src={`/images/${targets[0].id}.jpg`}
                      alt={targets[0].name || t('battleUI.actionHistory.target')}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Health display for damage effects */}
                    {action.effect?.effectType === 'damage' && (targets[0].healthBefore !== undefined || targets[0].damageTaken !== undefined) && (
                      <div className="action-health-display action-health-target">
                        {targets[0].healthBefore !== undefined && (
                          <span className="health-before">{targets[0].healthBefore}</span>
                        )}
                        {targets[0].damageTaken !== undefined && targets[0].damageTaken > 0 && (
                          <span className="health-damage">-{targets[0].damageTaken}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="tooltip-item-name">{targets[0].name || t('battleUI.actionHistory.target')}</div>
                </div>
              ) : (
                <div className="tooltip-targets">
                  {targets.map((target, idx) => (
                    <div key={idx} className="tooltip-target-item">
                      <div className="tooltip-target-image-wrapper">
                        <img
                          src={`/images/${target.id}.jpg`}
                          alt={target.name || t('battleUI.actionHistory.target')}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {/* Health display for damage effects */}
                        {action.effect?.effectType === 'damage' && (target.healthBefore !== undefined || target.damageTaken !== undefined) && (
                          <div className="action-health-display action-health-target">
                            {target.healthBefore !== undefined && (
                              <span className="health-before">{target.healthBefore}</span>
                            )}
                            {target.damageTaken !== undefined && target.damageTaken > 0 && (
                              <span className="health-damage">-{target.damageTaken}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="tooltip-item-name">{target.name || t('battleUI.actionHistory.target')}</div>
                    </div>
                  ))}
                </div>
              )
            ) : action.target ? (
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${action.target.id}.jpg`}
                    alt={action.target.name || t('battleUI.actionHistory.target')}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Health display for damage effects or stat effects that reduce health */}
                  {(() => {
                    const isDamageEffect = action.effect?.effectType === 'damage';
                    const isHealthReducingStatEffect = action.effect?.effectType === 'stat' && action.effect?.healthChange < 0;
                    const hasHealthInfo = action.target?.healthBefore !== undefined || action.target?.damageTaken !== undefined;
                    
                    if ((isDamageEffect || isHealthReducingStatEffect) && hasHealthInfo) {
                      return (
                        <div className="action-health-display action-health-target">
                          {action.target.healthBefore !== undefined && (
                            <span className="health-before">{action.target.healthBefore}</span>
                          )}
                          {action.target.damageTaken !== undefined && action.target.damageTaken > 0 && (
                            <span className="health-damage">-{action.target.damageTaken}</span>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div className="tooltip-item-name">{action.target.name || t('battleUI.actionHistory.target')}</div>
              </div>
            ) : null}
          </>
        ) : isGeneralInteractionAction ? (
          <>
            {/* Source image + name */}
            {action.source && (
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${action.source.id}.jpg`}
                    alt={action.source.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="tooltip-item-name">{action.source.name}</div>
              </div>
            )}
            
            {/* Effect text */}
            <div className="tooltip-enhance-text" style={{
              display: 'flex',
              alignItems: 'center',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'normal',
              padding: '8px 0'
            }}>
              {action.effectType === 'attack' && t('battleUI.actionHistory.attacked')}
              {action.effectType === 'heal' && t('battleUI.actionHistory.healedGeneralFor', { amount: action.amount })}
              {action.effectType === 'damage' && t('battleUI.actionHistory.dealtDamageToGeneral', { amount: action.amount })}
            </div>
            
            {/* General avatar */}
            <div className="tooltip-action-item">
              <div className="tooltip-target-image-wrapper">
                <img
                  src={getGeneralAvatarImage(action.playerId)}
                  alt={t('battleUI.actionHistory.general')}
                  onError={(e) => {
                    e.target.src = '/images/human_1.png';
                  }}
                />
                {/* General health display */}
                {action.general && (action.general.healthBefore !== undefined || action.general.damageTaken !== undefined) && (
                  <div className="action-health-display action-health-general">
                    {action.general.healthBefore !== undefined && (
                      <span className="health-before">{action.general.healthBefore}</span>
                    )}
                    {action.general.damageTaken !== undefined && action.general.damageTaken > 0 && (
                      <span className="health-damage">-{action.general.damageTaken}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="tooltip-item-name">{t('battleUI.actionHistory.general')}</div>
            </div>
          </>
        ) : isOrderProvidedAction ? (
          <>
            {/* Source image + name */}
            {action.source && (
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${action.source.id}.jpg`}
                    alt={action.source.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="tooltip-item-name">{action.source.name}</div>
              </div>
            )}
            
            {/* "provided" text */}
            <div className="tooltip-enhance-text" style={{
              display: 'flex',
              alignItems: 'center',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'normal',
              padding: '8px 0'
            }}>
              {t('battleUI.actionHistory.provided')}
            </div>
            
            {/* Order image + name */}
            {action.order && (
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${action.order.id}.jpg`}
                    alt={action.order.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="tooltip-item-name">{action.order.name}</div>
              </div>
            )}
          </>
        ) : isStrategemRequestedAction ? (
          <>
            {/* Strategem image + name */}
            {action.order && (
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${action.order.id}.jpg`}
                    alt={action.order.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="tooltip-item-name">{action.order.name}</div>
              </div>
            )}
            
            {/* "has been played on" text */}
            {(action.target || (action.targets && action.targets.length > 0)) && (
              <div className="tooltip-enhance-text" style={{
                display: 'flex',
                alignItems: 'center',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'normal',
                padding: '8px 0'
              }}>
                {t('battleUI.actionHistory.on', { defaultValue: 'on' })}
              </div>
            )}
            
            {/* Target unit(s) */}
            {action.target ? (
              // ✅ FIX: Handle general targets (for strategems like Kamikaze that damage generals)
              action.target.type === 'general' ? (
                <div className="tooltip-action-item">
                  <div className="tooltip-target-image-wrapper">
                    <img
                      src={getGeneralAvatarImage(action.target.playerId)}
                      alt={t('battleUI.actionHistory.general')}
                      onError={(e) => {
                        e.target.src = '/images/human_1.png';
                      }}
                    />
                    {/* ✅ FIX: Show health display for general damage/heal */}
                    {(action.target.healthBefore !== undefined || action.target.damageTaken !== undefined || action.target.healAmount !== undefined) && (
                      <div className="action-health-display action-health-general">
                        {action.target.healthBefore !== undefined && (
                          <span className="health-before">{action.target.healthBefore}</span>
                        )}
                        {action.target.damageTaken !== undefined && action.target.damageTaken > 0 && (
                          <span className="health-damage">-{action.target.damageTaken}</span>
                        )}
                        {action.target.healAmount !== undefined && action.target.healAmount > 0 && (
                          <span className="health-heal">+{action.target.healAmount}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="tooltip-item-name">{t('battleUI.actionHistory.general')}</div>
                </div>
              ) : (
                <div className="tooltip-action-item">
                  <div className="tooltip-target-image-wrapper">
                    <img
                      src={`/images/${action.target.id}.jpg`}
                      alt={action.target.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* ✅ FIX: Show skull only if target died AS A RESULT OF THIS ACTION */}
                    {/* For strategem actions, only show skull if the strategem actually killed the unit */}
                    {/* Strategems that only enhance (like Hive Surge ID 62) don't kill units, so no skull */}
                    {/* Strategems that kill directly have died: true but no damaged flag */}
                    {/* Strategems that deal damage have died: true and damaged: true */}
                    {action.target.died && action.type === 'strategem-requested' && 
                     action.order?.id !== 62 && // Hive Surge only enhances, doesn't kill
                     action.target.damaged !== false && // Don't show if explicitly marked as not damaged
                     (action.target.damaged === true || action.target.damaged === undefined) && (
                      <div className="skull-overlay">💀</div>
                    )}
                    {/* ✅ FIX: Show health display for strategem targets (damage/kill) */}
                    {(action.target.healthBefore !== undefined || action.target.damageTaken !== undefined) && (
                      <div className="action-health-display action-health-target">
                        {action.target.healthBefore !== undefined && (
                          <span className="health-before">{action.target.healthBefore}</span>
                        )}
                        {action.target.damageTaken !== undefined && action.target.damageTaken > 0 && (
                          <span className="health-damage">-{action.target.damageTaken}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="tooltip-item-name">{action.target.name}</div>
                </div>
              )
            ) : action.targets && action.targets.length > 0 ? (
              <div className="tooltip-targets">
                {action.targets.map((target, idx) => {
                  // ✅ FIX: Handle general targets (for strategems like Kamikaze that damage generals)
                  if (target.type === 'general') {
                    return (
                      <div key={idx} className="tooltip-target-item tooltip-general-item">
                        <div className="tooltip-target-image-wrapper">
                          <img
                            src={getGeneralAvatarImage(target.playerId)}
                            alt={t('battleUI.actionHistory.general')}
                            onError={(e) => {
                              e.target.src = '/images/human_1.png';
                            }}
                          />
                          {/* ✅ FIX: Show health display for general damage/heal */}
                          {(target.healthBefore !== undefined || target.damageTaken !== undefined || target.healAmount !== undefined) && (
                            <div className="action-health-display action-health-general">
                              {target.healthBefore !== undefined && (
                                <span className="health-before">{target.healthBefore}</span>
                              )}
                              {target.damageTaken !== undefined && target.damageTaken > 0 && (
                                <span className="health-damage">-{target.damageTaken}</span>
                              )}
                              {target.healAmount !== undefined && target.healAmount > 0 && (
                                <span className="health-heal">+{target.healAmount}</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="tooltip-item-name">{t('battleUI.actionHistory.general')}</div>
                      </div>
                    );
                  }
                  
                  // Regular unit target
                  return (
                    <div key={idx} className="tooltip-target-item">
                      <div className="tooltip-target-image-wrapper">
                        <img
                          src={`/images/${target.id}.jpg`}
                          alt={target.name}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {/* ✅ FIX: Show skull only if target died AS A RESULT OF THIS ACTION */}
                        {/* For strategem actions, only show skull if the strategem actually killed the unit */}
                        {/* Strategems that only enhance (like Hive Surge ID 62) don't kill units, so no skull */}
                        {/* Strategems that kill directly have died: true but no damaged flag */}
                        {/* Strategems that deal damage have died: true and damaged: true */}
                        {target.died && action.type === 'strategem-requested' && 
                         action.order?.id !== 62 && // Hive Surge only enhances, doesn't kill
                         target.damaged !== false && // Don't show if explicitly marked as not damaged
                         (target.damaged === true || target.damaged === undefined) && (
                          <div className="skull-overlay">💀</div>
                        )}
                        {/* ✅ FIX: Show health display for strategem targets (damage/kill) */}
                        {(target.healthBefore !== undefined || target.damageTaken !== undefined) && (
                          <div className="action-health-display action-health-target">
                            {target.healthBefore !== undefined && (
                              <span className="health-before">{target.healthBefore}</span>
                            )}
                            {target.damageTaken !== undefined && target.damageTaken > 0 && (
                              <span className="health-damage">-{target.damageTaken}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="tooltip-item-name">{target.name}</div>
                    </div>
                  );
                })}
              </div>
            ) : message ? (
              <div className="tooltip-enhance-text" style={{
                display: 'flex',
                alignItems: 'center',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'normal',
                padding: '8px 0'
              }}>
                {message.replace(action.order.name + ' ', '')}
              </div>
            ) : null}
          </>
        ) : isWeaponEquippedAction ? (
          <>
            {/* Weapon image + name */}
            <div className="tooltip-action-item">
              <div className="tooltip-target-image-wrapper">
                <img
                  src={`/images/${action.weapon.id}.jpg`}
                  alt={action.weapon.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="tooltip-item-name">{action.weapon.name}</div>
            </div>
            
            {/* "equipped to" text */}
            <div className="tooltip-enhance-text" style={{
              display: 'flex',
              alignItems: 'center',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'normal',
              padding: '8px 0'
            }}>
              {t('battleUI.actionHistory.equippedTo')}
            </div>
            
            {/* Target unit image + name */}
            {action.target && (
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${action.target.id}.jpg`}
                    alt={action.target.name || t('battleUI.actionHistory.target')}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <div className="tooltip-item-name">{action.target.name || t('battleUI.actionHistory.target')}</div>
              </div>
            )}
          </>
        ) : isEnhancedAction ? (
          <>
            {/* Enhancer image + name */}
            <div className="tooltip-action-item">
              <div className="tooltip-target-image-wrapper">
                <img
                  src={`/images/${actionItem.id}.jpg`}
                  alt={actionItem.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="tooltip-item-name">{actionItem.name}</div>
            </div>
            
            {/* Effect text based on effect type */}
            <div className="tooltip-enhance-text" style={{
              display: 'flex',
              alignItems: 'center',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'normal',
              padding: '8px 0'
            }}>
              {action.effect?.effectType === 'ability' && t('battleUI.actionHistory.gaveAbilityTo', { ability: action.effect.ability })}
              {action.effect?.effectType === 'stat' && (
                (() => {
                  const attackText = action.effect.attackChange > 0 ? `+${action.effect.attackChange}` : `${action.effect.attackChange}`;
                  const healthText = action.effect.healthChange > 0 ? `+${action.effect.healthChange}` : `${action.effect.healthChange}`;
                  if (action.effect.attackChange === 0 && action.effect.healthChange !== 0) {
                    return t('battleUI.actionHistory.providedHealthTo', { health: healthText });
                  } else if (action.effect.healthChange === 0 && action.effect.attackChange !== 0) {
                    return t('battleUI.actionHistory.providedAttackTo', { attack: attackText });
                  } else {
                    return t('battleUI.actionHistory.providedStatsTo', { attack: attackText, health: healthText });
                  }
                })()
              )}
              {action.effect?.effectType === 'status' && (
                action.effect.statuses && action.effect.statuses.length > 1
                  ? action.effect.statuses.map(s => {
                      if (s === 'confused') return t('battleUI.actionHistory.confused');
                      if (s === 'immobilized') return t('battleUI.actionHistory.immobilized');
                      return s;
                    }).join(' and ')
                  : (() => {
                      const status = action.effect.status || action.effect.statuses?.[0];
                      if (status === 'confused') return t('battleUI.actionHistory.confused');
                      if (status === 'immobilized') return t('battleUI.actionHistory.immobilized');
                      return status || t('battleUI.actionHistory.affected');
                    })()
              )}
              {action.effect?.effectType === 'damage' && t('battleUI.actionHistory.dealtDamageTo', { amount: action.effect.amount })}
              {action.effect?.effectType === 'shield' && t('battleUI.actionHistory.gaveShieldTo')}
            </div>
            
            {/* Target(s) - show all targets for grouped effects */}
            {action.targets && action.targets.length > 1 ? (
              // Multiple targets - show all in a row
              <div className="tooltip-targets">
                {action.targets.map((target, idx) => (
                  <div key={idx} className="tooltip-target-item">
                    <div className="tooltip-target-image-wrapper">
                      <img
                        src={`/images/${target.id}.jpg`}
                        alt={target.name || t('battleUI.actionHistory.target')}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {/* Health display for damage effects or stat effects that reduce health */}
                      {(() => {
                        const isDamageEffect = action.effect?.effectType === 'damage';
                        const isHealthReducingStatEffect = action.effect?.effectType === 'stat' && action.effect?.healthChange < 0;
                        const hasHealthInfo = target.healthBefore !== undefined || target.damageTaken !== undefined;
                        
                        if ((isDamageEffect || isHealthReducingStatEffect) && hasHealthInfo) {
                          return (
                            <div className="action-health-display action-health-target">
                              {target.healthBefore !== undefined && (
                                <span className="health-before">{target.healthBefore}</span>
                              )}
                              {target.damageTaken !== undefined && target.damageTaken > 0 && (
                                <span className="health-damage">-{target.damageTaken}</span>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                    <div className="tooltip-item-name">{target.name || t('battleUI.actionHistory.target')}</div>
                  </div>
                ))}
              </div>
            ) : (action.targets && action.targets.length === 1) ? (
              // Single target in targets array - show image + name
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${action.targets[0].id}.jpg`}
                    alt={action.targets[0].name || t('battleUI.actionHistory.target')}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Health display for damage effects or stat effects that reduce health */}
                  {(() => {
                    const isDamageEffect = action.effect?.effectType === 'damage';
                    const isHealthReducingStatEffect = action.effect?.effectType === 'stat' && action.effect?.healthChange < 0;
                    const hasHealthInfo = action.targets[0]?.healthBefore !== undefined || action.targets[0]?.damageTaken !== undefined;
                    
                    if ((isDamageEffect || isHealthReducingStatEffect) && hasHealthInfo) {
                      return (
                        <div className="action-health-display action-health-target">
                          {action.targets[0].healthBefore !== undefined && (
                            <span className="health-before">{action.targets[0].healthBefore}</span>
                          )}
                          {action.targets[0].damageTaken !== undefined && action.targets[0].damageTaken > 0 && (
                            <span className="health-damage">-{action.targets[0].damageTaken}</span>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div className="tooltip-item-name">{action.targets[0].name || t('battleUI.actionHistory.target')}</div>
              </div>
            ) : action.target ? (
              // Single target - show image + name
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${action.target.id}.jpg`}
                    alt={action.target.name || t('battleUI.actionHistory.target')}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Health display for damage effects or stat effects that reduce health */}
                  {(() => {
                    const isDamageEffect = action.effect?.effectType === 'damage';
                    const isHealthReducingStatEffect = action.effect?.effectType === 'stat' && action.effect?.healthChange < 0;
                    const hasHealthInfo = action.target?.healthBefore !== undefined || action.target?.damageTaken !== undefined;
                    
                    if ((isDamageEffect || isHealthReducingStatEffect) && hasHealthInfo) {
                      return (
                        <div className="action-health-display action-health-target">
                          {action.target.healthBefore !== undefined && (
                            <span className="health-before">{action.target.healthBefore}</span>
                          )}
                          {action.target.damageTaken !== undefined && action.target.damageTaken > 0 && (
                            <span className="health-damage">-{action.target.damageTaken}</span>
                          )}
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
                <div className="tooltip-item-name">{action.target.name || t('battleUI.actionHistory.target')}</div>
              </div>
            ) : null}
          </>
        ) : isKillAction ? (
          <>
            {/* Killer image + name */}
            <div className="tooltip-action-item">
              <div className="tooltip-target-image-wrapper">
                <img
                  src={`/images/${actionItem.id}.jpg`}
                  alt={actionItem.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {actionItem.died && (
                  <div className="skull-overlay">💀</div>
                )}
              </div>
              <div className="tooltip-item-name">{actionItem.name}</div>
            </div>
            
            {/* "has destroyed" text */}
            <div className="tooltip-kill-text" style={{
              display: 'flex',
              alignItems: 'center',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'normal',
              padding: '8px 0'
            }}>
              {t('battleUI.actionHistory.hasDestroyed')}
            </div>
            
            {/* Target(s) - show all targets for grouped kills */}
            {targets.length === 1 ? (
              // Single target - show image + name
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${targets[0].id}.jpg`}
                    alt={targets[0].name || t('battleUI.actionHistory.target')}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Target always died if it's a kill action */}
                  {targets[0].died !== false && (
                    <div className="skull-overlay">💀</div>
                  )}
                  {/* Health display for killed units */}
                  {(targets[0].healthBefore !== undefined || targets[0].damageTaken !== undefined) && (
                    <div className="action-health-display action-health-target">
                      {targets[0].healthBefore !== undefined && (
                        <span className="health-before">{targets[0].healthBefore}</span>
                      )}
                      {targets[0].damageTaken !== undefined && targets[0].damageTaken > 0 && (
                        <span className="health-damage">-{targets[0].damageTaken}</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="tooltip-item-name">{targets[0].name || t('battleUI.actionHistory.target')}</div>
              </div>
            ) : (
              // Multiple targets - show all in a row
              <div className="tooltip-targets">
                {targets.map((target, idx) => (
                  <div key={idx} className="tooltip-target-item">
                    <div className="tooltip-target-image-wrapper">
                      <img
                        src={`/images/${target.id}.jpg`}
                        alt={target.name || t('battleUI.actionHistory.target')}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {/* Target always died if it's a kill action */}
                      {target.died !== false && (
                        <div className="skull-overlay">💀</div>
                      )}
                      {/* Health display for killed units */}
                      {(target.healthBefore !== undefined || target.damageTaken !== undefined) && (
                        <div className="action-health-display action-health-target">
                          {target.healthBefore !== undefined && (
                            <span className="health-before">{target.healthBefore}</span>
                          )}
                          {target.damageTaken !== undefined && target.damageTaken > 0 && (
                            <span className="health-damage">-{target.damageTaken}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="tooltip-item-name">{target.name || t('battleUI.actionHistory.target')}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : isAttackAction ? (
          <>
            {/* Attacker image + name */}
        <div className="tooltip-action-item">
          <div className="tooltip-target-image-wrapper">
            <img
              src={`/images/${actionItem.id}.jpg`}
              alt={actionItem.name}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            {actionItem.died && (
              <div className="skull-overlay">💀</div>
            )}
            {/* Attacker health display */}
            {(action.attacker || actionItem) && ((action.attacker?.healthBefore !== undefined || actionItem?.healthBefore !== undefined) || (action.attacker?.damageTaken !== undefined || actionItem?.damageTaken !== undefined)) && (
              <div className="action-health-display action-health-attacker">
                {(action.attacker?.healthBefore !== undefined || actionItem?.healthBefore !== undefined) && (
                  <span className="health-before">{action.attacker?.healthBefore ?? actionItem?.healthBefore}</span>
                )}
                {((action.attacker?.damageTaken !== undefined || actionItem?.damageTaken !== undefined) && (action.attacker?.damageTaken > 0 || actionItem?.damageTaken > 0)) && (
                  <span className="health-damage">-{action.attacker?.damageTaken ?? actionItem?.damageTaken}</span>
                )}
              </div>
            )}
          </div>
          <div className="tooltip-item-name">{actionItem.name}</div>
        </div>

            {/* "has attacked" text */}
            <div className="tooltip-attack-text" style={{
              display: 'flex',
              alignItems: 'center',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'normal',
              padding: '8px 0'
            }}>
              {t('battleUI.actionHistory.hasAttacked')}
            </div>
            
            {/* Target image + name */}
            {targets[0] && (
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${targets[0].id}.jpg`}
                    alt={targets[0].name || t('battleUI.actionHistory.target')}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Show skull if target died in this attack */}
                  {targets[0].died && (
                    <div className="skull-overlay">💀</div>
                  )}
                  {/* Target health display */}
                  {(action.target || targets[0]) && ((action.target?.healthBefore !== undefined || targets[0]?.healthBefore !== undefined) || (action.target?.damageTaken !== undefined || targets[0]?.damageTaken !== undefined)) && (
                    <div className="action-health-display action-health-target">
                      {(action.target?.healthBefore !== undefined || targets[0]?.healthBefore !== undefined) && (
                        <span className="health-before">{action.target?.healthBefore ?? targets[0]?.healthBefore}</span>
                      )}
                      {((action.target?.damageTaken !== undefined || targets[0]?.damageTaken !== undefined) && (action.target?.damageTaken > 0 || targets[0]?.damageTaken > 0)) && (
                        <span className="health-damage">-{action.target?.damageTaken ?? targets[0]?.damageTaken}</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="tooltip-item-name">{targets[0].name || t('battleUI.actionHistory.target')}</div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Left side: Action item (unit image + name) */}
            {actionItem && (
              <div className="tooltip-action-item">
                <div className="tooltip-target-image-wrapper">
                  <img
                    src={`/images/${actionItem.id}.jpg`}
                    alt={actionItem.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {actionItem.died && (
                    <div className="skull-overlay">💀</div>
                  )}
                </div>
                <div className="tooltip-item-name">{actionItem.name}</div>
              </div>
            )}

            {/* Right side: Deployment action text or Targets */}
            {isGroupedDeployment ? (
              <>
                {/* "deployed" text */}
                <div className="tooltip-deployment-text" style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 'normal',
                  padding: '8px 0'
                }}>
                  deployed
                </div>
                {/* All deployed units */}
        {targets && targets.length > 0 && (
                  <div className="tooltip-targets">
                    {targets.map((target, idx) => (
                      <div key={idx} className="tooltip-target-item">
                        <div className="tooltip-target-image-wrapper">
                          <img
                            src={`/images/${target.id}.jpg`}
                            alt={target.name || t('battleUI.actionHistory.target')}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                        <div className="tooltip-item-name">{target.name || t('battleUI.actionHistory.target')}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : isDroneDeployedAction ? (
              <div className="tooltip-deployment-text" style={{
                display: 'flex',
                alignItems: 'center',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'normal',
                padding: '8px 0'
              }}>
                {t('battleUI.actionHistory.hasBeenDeployed')}
              </div>
            ) : deploymentActionText ? (
              <div className="tooltip-deployment-text" style={{
                display: 'flex',
                alignItems: 'center',
                color: '#fff',
                fontSize: '14px',
                fontWeight: 'normal',
                padding: '8px 0'
              }}>
                {deploymentActionText}
              </div>
            ) : targets && targets.length > 0 && actionItem && (
          <div className="tooltip-targets">
            {targets.map((target, idx) => {
              // Handle deployment zone messages (for unit/drone deployments without actual targets)
              if (target.type === 'deployment-zone') {
                return (
                  <div key={idx} className="tooltip-target-item">
                    <div className="tooltip-item-name">{target.message || t('battleUI.actionHistory.deployedTo', { zone: target.zoneName || target.zone })}</div>
                  </div>
                );
              } else if (target.id) {
                return (
                  <div key={idx} className="tooltip-target-item">
                    <div className="tooltip-target-image-wrapper">
                      <img
                        src={`/images/${target.id}.jpg`}
                        alt={target.name || t('battleUI.actionHistory.target')}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      {/* ✅ FIX: Only show skull for actions that actually killed the unit */}
                      {/* For strategem actions, only show skull if the strategem actually killed the unit */}
                      {/* Strategems that only enhance (like Hive Surge ID 62) don't kill units, so no skull */}
                      {/* Strategems that kill directly have died: true but no damaged flag */}
                      {/* Strategems that deal damage have died: true and damaged: true */}
                      {target.died && (
                        (action.type === 'unit-killed' || 
                         action.type === 'unit-attacked' || 
                         (action.type === 'strategem-requested' && 
                          action.order?.id !== 62 && // Hive Surge only enhances, doesn't kill
                          target.damaged !== false && // Don't show if explicitly marked as not damaged
                          (target.damaged === true || target.damaged === undefined)))
                      ) && (
                        <div className="skull-overlay">💀</div>
                      )}
                      {(target.hasShield || target.receivedShield) && (
                        <div className="shield-overlay" style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          pointerEvents: 'none'
                        }}>🛡️</div>
                      )}
                    </div>
                    <div className="tooltip-item-name">
                      {target.name || t('battleUI.actionHistory.target')}
                      {target.type === 'hand' && ' (Added to Hand)'}
                      {(target.hasShield || target.receivedShield) && ' 🛡️'}
                      {target.receivedBuff && ' (Buffed)'}
                    </div>
                  </div>
                );
              } else if (target.type === 'general') {
                return (
                  <div key={idx} className="tooltip-target-item tooltip-general-item">
                    <div className="tooltip-target-image-wrapper">
                      <img
                        src={getGeneralAvatarImage(target.playerId)}
                        alt={t('battleUI.actionHistory.general')}
                        onError={(e) => {
                          e.target.src = '/images/human_1.png';
                        }}
                      />
                    </div>
                    <div className="tooltip-item-name">{t('battleUI.actionHistory.general')}</div>
                  </div>
                );
              } else {
                return (
                  <div key={idx} className="tooltip-target-item">
                    <div className="tooltip-item-name">{target.name || t('battleUI.actionHistory.target')}</div>
                  </div>
                );
              }
            })}
          </div>
        )}
          </>
        )}

      </div>
    </div>
  );
}

export default ActionHistoryTooltip;

